import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { STAGES, fmtDate } from "@/lib/constants";
import { TrackBadge } from "@/components/TrackBadge";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bell, GripVertical } from "lucide-react";

const STAGE_COLOR = {
  sourced: "#3b82f6", approved: "#8b5cf6", submitted: "#14b8a6",
  replied: "#f59e0b", booked: "#10b981", archived: "#64748b",
};

export default function CRM() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: opps = [] } = useQuery({ queryKey: ["crm-opps"], queryFn: async () => (await api.get("/opportunities")).data });
  const { data: reminders = [] } = useQuery({ queryKey: ["reminders"], queryFn: async () => (await api.get("/reminders")).data });

  const move = async (id, stage) => {
    await api.patch(`/opportunities/${id}`, { pipeline_stage: stage });
    toast.success(`Moved to ${stage}`);
    qc.invalidateQueries({ queryKey: ["crm-opps"] });
  };

  const openReminders = reminders.filter((r) => !r.done);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="crm-view">
      <header className="flex items-end justify-between flex-wrap gap-3 max-w-[1400px] mx-auto w-full">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">CRM Pipeline</h1>
          <p className="text-sm text-muted-foreground mt-1">Drag-free stage control with follow-up reminders. Sourced → Booked.</p>
        </div>
        {openReminders.length > 0 && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 flex items-center gap-2 text-sm" data-testid="reminders-banner">
            <Bell className="h-4 w-4 text-amber-400" />
            <span className="text-amber-300">{openReminders.length} follow-up{openReminders.length > 1 ? "s" : ""} pending</span>
          </div>
        )}
      </header>

      <div className="overflow-x-auto pb-4 max-w-[1400px] mx-auto w-full">
        <div className="flex gap-4 min-w-max">
          {STAGES.map((stage) => {
            const col = opps.filter((o) => o.pipeline_stage === stage.id);
            return (
              <div key={stage.id} className="w-[280px] shrink-0" data-testid={`crm-stage-column-${stage.id}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: STAGE_COLOR[stage.id] }} />
                    <span className="font-heading font-semibold text-sm">{stage.label}</span>
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">{col.length}</span>
                </div>
                <div className="space-y-2.5 rounded-xl bg-muted/20 p-2 min-h-[120px]">
                  {col.map((o) => (
                    <Card key={o.id} className="p-3 border-border hover:bg-accent/50 transition-colors" data-testid={`crm-card-${o.id}`}>
                      <button onClick={() => navigate(`/opportunity/${o.id}`)} className="text-sm font-medium text-left hover:text-primary line-clamp-2">{o.title}</button>
                      <div className="text-xs text-muted-foreground mt-1">{o.org_name}</div>
                      <div className="flex items-center justify-between mt-2">
                        <TrackBadge track={o.track} short />
                        <span className="text-xs font-mono text-muted-foreground">${(o.fee_potential || 0).toLocaleString()}</span>
                      </div>
                      <select
                        value={o.pipeline_stage}
                        onChange={(e) => move(o.id, e.target.value)}
                        data-testid={`crm-move-${o.id}`}
                        className="mt-2.5 w-full text-xs rounded-md border border-border bg-background px-2 py-1.5 text-muted-foreground"
                      >
                        {STAGES.map((s) => <option key={s.id} value={s.id}>Move to {s.label}</option>)}
                      </select>
                    </Card>
                  ))}
                  {col.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">Empty</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Reminders list */}
      <Card className="p-5 border-border max-w-[1400px] mx-auto w-full">
        <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><Bell className="h-4 w-4" /> Follow-up reminders</h2>
        <div className="space-y-2">
          {reminders.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-lg border border-border p-3" data-testid={`crm-reminder-${r.id}`}>
              <input type="checkbox" checked={!!r.done} onChange={async () => { await api.patch(`/reminders/${r.id}`, { done: !r.done }); qc.invalidateQueries({ queryKey: ["reminders"] }); }} className="accent-primary" />
              <div className={`flex-1 ${r.done ? "line-through opacity-60" : ""}`}>
                <div className="text-sm">{r.note}</div>
                <button onClick={() => navigate(`/opportunity/${r.opportunity?.id}`)} className="text-xs text-muted-foreground hover:text-primary">{r.opportunity?.title} · {r.opportunity?.org_name}</button>
              </div>
              <Badge variant="outline" className="text-xs">{fmtDate(r.due_date)}</Badge>
            </div>
          ))}
          {reminders.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No reminders scheduled.</p>}
        </div>
      </Card>
    </div>
  );
}
